/**
 * DigitalSummaryStep — Step 1 (summary) of the digital checkout variant.
 *
 * Renders:
 *   - Order summary card (product title + seller + the emerald
 *     "Descarga inmediata tras la compra" banner + the "Pago único: X
 *     créditos" total).
 *   - Escrow explanation paragraph (auto-release on download OR 24h
 *     fallback auto-release).
 *   - Inline error alert + footer (Cancelar / Continuar → verify step).
 *
 * Founder directive (2026-11): 2FA removed from checkout — no
 * marketplace requires 2FA to buy. The "Verificar 2FA" button is now a
 * "Continuar" button that transitions directly to the verify step.
 * The TwoFaActivationRequiredCard (2FA enrollment gate) was also
 * removed.
 *
 * SPEC-244 · partición VERBATIM de
 * frontend/src/components/market/checkout-modal.tsx (2.637 L).
 *
 * Preserved VERBATIM — the original summary JSX block (lines 2471-2542
 * of the monolith).
 *
 * Anti-P-041: 0 `as any`. Anti-P-042: 0 `console.*`.
 * Anti-P-366: only adds the 2FA pre-check (Bug #2 fix).
 */

import { Button } from '@/components/ui/button';
import { DialogFooter } from '@/components/ui/dialog';
import { ArrowRight, Download } from 'lucide-react';
import type { PublicProductResponse } from '@profileia/shared';
import { formatCredits } from '../helpers';
import { SubmitErrorAlert } from './submit-error-alert';

// ─── Component ───────────────────────────────────────────────────────────────

export interface DigitalSummaryStepProps {
  product: PublicProductResponse;
  grandTotal: number;
  submitting: boolean;
  submitError: string | null;
  onClose: () => void;
  /** Transitions to the verify step (Founder directive 2026-11: 2FA
   * removed — this used to open the 2FA dialog, now it just advances). */
  onContinue: () => void;
}

export function DigitalSummaryStep({
  product,
  grandTotal,
  submitting,
  submitError,
  onClose,
  onContinue,
}: DigitalSummaryStepProps) {
  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border bg-muted/40 p-4 space-y-2">
        <p className="text-sm font-medium line-clamp-2">{product.title}</p>
        <p className="text-xs text-muted-foreground">
          Vendido por <span className="font-medium">{product.seller.storeName}</span>
        </p>
        <div className="rounded-md border border-emerald-200 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/20 p-2.5 text-xs flex items-start gap-2">
          <Download className="size-3.5 text-emerald-600 dark:text-emerald-400 mt-0.5 shrink-0" aria-hidden="true" />
          <span className="text-emerald-800 dark:text-emerald-300">
            Descarga inmediata tras la compra. Recibirás un enlace firmado válido por 15 minutos.
          </span>
        </div>
        <dl className="space-y-1.5 text-sm mt-2">
          <div className="border-t border-border pt-2 flex justify-between text-base">
            <dt className="font-semibold">Pago único</dt>
            <dd className="font-bold tabular-nums">
              {formatCredits(grandTotal)} créditos
            </dd>
          </div>
        </dl>
      </div>

      <p className="text-xs text-muted-foreground">
        Al confirmar, el pago se debita a una cuenta de
        <strong> escrow</strong>. El escrow se libera al vendedor
        automáticamente al descargar (o en 24h si no descargas).
      </p>

      {submitError && <SubmitErrorAlert submitError={submitError} />}

      <DialogFooter className="sticky bottom-0 -mx-6 -mb-6 mt-2 flex-col gap-2 border-t border-border bg-background px-6 py-3 sm:flex-col">
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          className="flex-1"
          disabled={submitting}
        >
          Cancelar
        </Button>
        <Button
          type="button"
          className="flex-1"
          onClick={onContinue}
          disabled={submitting}
        >
          Continuar
          <ArrowRight className="size-4 ml-1" aria-hidden="true" />
        </Button>
      </DialogFooter>
    </div>
  );
}
